import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Typography } from '@mui/material';
import { useRowsCreatives } from 'features/creatives';
import type { PackEntry } from 'types/shared';
import { Network } from '../../../../constants';
import { isScalingEgProcess } from '../../utils/marketingProcess';
import {
  countCreativesWithoutLocalizations,
  getDloNoLocalizationsHintMessage,
  isDloAdType,
} from '../../utils/dloCreativeSelection';

interface DloNoLocalizationsSearchHintProps {
  filterFieldName: string;
}

const DloNoLocalizationsSearchHint: FC<DloNoLocalizationsSearchHintProps> = ({
  filterFieldName,
}) => {
  const { watch } = useFormContext();
  const marketingProcess = watch('marketingProcess') as string;
  const isHypothesisTestingProcess = marketingProcess === 'hypothesis_testing';
  const isScalingEg = isScalingEgProcess(marketingProcess);
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number | null;
  const adType = isScalingEg
    ? (watch('adType') as string)
    : (packsWithCreatives?.[activePacksWithCreatives ?? 0]?.adType ?? '');
  const filterTerms = watch(filterFieldName) as string[] | undefined;

  const { rows } = useRowsCreatives({
    params: {
      network: Network.FACEBOOK,
      ...(isHypothesisTestingProcess
        ? { forceEnabled: true, with_hypothesis: true }
        : {}),
    },
  });

  const hintMessage = useMemo(() => {
    if (!isDloAdType(adType) || !filterTerms?.length || !rows?.length) {
      return null;
    }

    const count = countCreativesWithoutLocalizations(rows);
    return count > 0 ? getDloNoLocalizationsHintMessage(count) : null;
  }, [adType, filterTerms, rows]);

  if (!hintMessage) {
    return null;
  }

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="dloNoLocalizationsHint"
      sx={{ mt: 0.5 }}
    >
      {hintMessage}
    </Typography>
  );
};

export default DloNoLocalizationsSearchHint;
